import { Box, Heading, Text } from '@chakra-ui/layout';
import { Button, Input, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import { StyledDropzone } from '../components/StyledDropzone';
import SelectedGameContext from '../context/SelectedGameContext';
import * as yup from 'yup';
import { useKeycloak } from '@react-keycloak/web';
import { useNavigate } from 'react-router-dom';
import { uploadToS3 } from '../util/depot';
import { AssetFileType } from '../util/depot';

const AddUnityActorDepotSchema = yup.object().shape({
    description: yup.string().required('Description is required'),
    rmb: yup.mixed().required('RMB Actor file is required'),
    lambda: yup.mixed().required('Lambda Actor file is required'),
});

export const AddUnityActorDepot: React.FC<{}> = () => {
    const selectedGame = useContext(SelectedGameContext);
    const [description, setDescription] = useState('');
    const [isRMBSet, setRMBReady] = useState(false);
    const [isLambdaSet, setLambdaReady] = useState(false);
    const [rmbCaption, setRMBCaption] = useState(
        "Drag 'n' drop RMB actor executable (Unity build in zip file) here, or click to select a file",
    );
    const [lambdaCaption, setLambdaCaption] = useState(
        "Drag 'n' drop Lambda actor executable (Unity build in zip file) here, or click to select a file",
    );
    const { keycloak } = useKeycloak();
    const toast = useToast();
    const navigate = useNavigate();

    const _uploadToS3 = async (desc: string, rmbFile: any, lambdaFile: any) => {
        const uploadingToastId = toast({
            title: 'Uploading...',
            status: 'info',
            duration: null,
            isClosable: true,
        });

        try {
            const fileTypes = [AssetFileType.unity_rmb_actor, AssetFileType.unity_lambda_actor];
            const files = [rmbFile, lambdaFile];
            const buildData = await uploadToS3(
                selectedGame.currLicense.key,
                desc,
                fileTypes,
                files,
                keycloak.token,
            );
            toast({
                title: 'Success!',
                description: `Build ${buildData?.buildId} uploaded successfully`,
                status: 'success',
                duration: 9000,
                isClosable: true,
            });
            toast.close(uploadingToastId || '');
            navigate(`/game/${selectedGame.currLicense.key}/csharp-actor`, {
                replace: true,
            });
        } catch (err) {
            return toast({
                title: 'Error',
                description: err instanceof Error ? err.message : `Unexpected error ${err}`,
                status: 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            toast.close(uploadingToastId || '');
        }
    };

    return (
        <Box>
            <Heading>Upload Unity Actor</Heading>
            <Formik
                initialValues={{
                    description: '',
                    rmb: null,
                    lambda: null,
                }}
                validationSchema={AddUnityActorDepotSchema}
                onSubmit={async (values) => {
                    await _uploadToS3(values.description, values.rmb, values.lambda);
                }}
            >
                {({ errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => {
                    const onRMBSelected = (acceptedFiles: any) => {
                        setFieldValue('rmb', acceptedFiles[0]);
                        setRMBCaption(acceptedFiles[0].name);
                        setRMBReady(true);
                    };

                    const onLambdaSelected = (acceptedFiles: any) => {
                        setFieldValue('lambda', acceptedFiles[0]);
                        setLambdaCaption(acceptedFiles[0].name);
                        setLambdaReady(true);
                    };

                    return (
                        <Form
                            action=""
                            style={{
                                padding: '1em',
                            }}
                        >
                            <Heading as="h2" size="md" paddingBottom="0.5em">
                                RMB Actor
                            </Heading>
                            <Text color="red">{errors.rmb && touched.rmb && errors.rmb}</Text>
                            <StyledDropzone
                                name="rmb"
                                caption={rmbCaption}
                                onDrop={onRMBSelected}
                            />

                            <Heading as="h2" size="md" paddingBottom="0.5em">
                                Lambda Actor
                            </Heading>
                            <Text color="red">
                                {errors.lambda && touched.lambda && errors.lambda}
                            </Text>
                            <StyledDropzone
                                name="lambda"
                                caption={lambdaCaption}
                                onDrop={onLambdaSelected}
                            />

                            <Heading as="h2" size="md" paddingBottom="0.5em">
                                Description
                            </Heading>
                            <Text color="red">
                                {errors.description && touched.description && errors.description}
                            </Text>
                            <Input
                                name="description"
                                placeholder="Short description of this build, such as version number"
                                marginBottom="0.5em"
                                onChange={(e) => {
                                    setDescription(e.target.value);
                                    handleChange(e);
                                }}
                                value={description}
                                onBlur={(e) => {
                                    handleBlur(e);
                                }}
                            />

                            <Button
                                type="submit"
                                marginTop="0.5em"
                                disabled={isSubmitting || !isRMBSet || !isLambdaSet || !description}
                            >
                                Upload
                            </Button>
                        </Form>
                    );
                }}
            </Formik>
        </Box>
    );
};
